import { useEffect, useMemo, useState } from 'react';
import type { ApiClient } from '../../api';
import type {
  MsrAnlageZeile, MsrAspZeile, MsrDokument, MsrDokumentart, MsrDokumentlauf,
} from './typen';
import { UnterlagenZipKnopf } from '../../UnterlagenZipKnopf';
import { zeitpunkt } from './kopf';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState, Feld, FilterBar,
  FormSection, StatusBadge, formatiereZahl, type FilterDefinition, type FilterWerte, type Spalte,
} from '../../muster';

/** Beschriftung je Dokumentart (Spezifikation 15.2). */
const ART_TEXT: Record<MsrDokumentart, string> = {
  schema: 'MSR-Schema', dpliste: 'Datenpunktliste (VDI 3814)', aksliste: 'AKS-Liste',
  funktionsbeschreibung: 'Funktionsbeschreibung', einstellwerte: 'Einstellwerte',
  anlagenheft: 'Anlagenheft', schaltplan: 'Übersichtsschaltplan', aspblatt: 'ASP-Blatt',
  klemmen: 'Klemmenbelegung', feldgeraete: 'Feldgeräteliste', aspheft: 'ASP-Heft',
  klemmenplan: 'Klemmenplan', kabelliste: 'Kabelliste', ispliste: 'Datenpunktliste je ISP',
  bacnet: 'BACnet-Objektliste',
  topologie: 'Topologie', ispsumme: 'ISP-Summenliste', revisionsgliederung: 'Gliederung Revisionsunterlagen',
  projektheft: 'Projektheft',
};

const ARTEN_ANLAGE: MsrDokumentart[] =
  ['schema', 'dpliste', 'aksliste', 'funktionsbeschreibung', 'einstellwerte', 'anlagenheft'];
const ARTEN_ASP: MsrDokumentart[] =
  ['schaltplan', 'aspblatt', 'klemmen', 'feldgeraete', 'klemmenplan', 'kabelliste', 'aspheft'];
const ARTEN_PROJEKT: MsrDokumentart[] = ['topologie', 'revisionsgliederung', 'projektheft'];

/**
 * F127 (R53): Text der Marke je Zielkennzeichen aus `msr_dokumente.Hinweis`
 * (`ziel_entfallen`, `ziel_geloescht`); `null` für alle anderen Hinweise.
 */
export function zielkennzeichenText(hinweis?: string | null): string | null {
  if (hinweis === 'ziel_entfallen') return 'Ziel entfallen';
  if (hinweis === 'ziel_geloescht') return 'Ziel gelöscht';
  return null;
}

const ZIELKENNZEICHEN_TITEL = 'Nicht mehr aktuell: Die Anlage bzw. der ASP ist entfallen oder gelöscht. '
  + 'Das Blatt bleibt zum Nachweis abgelegt; im ZIP liegt es im Ordner „Entfallen/".';

/** Dateigröße: B ganzzahlig, KB mit einer, MB mit zwei Nachkommastellen (über `formatiereZahl`, S1.7). */
export function groesseText(bytes: number): string {
  if (bytes < 1024) return `${formatiereZahl(bytes, 0)} B`;
  if (bytes < 1024 * 1024) return `${formatiereZahl(bytes / 1024, 1)} KB`;
  return `${formatiereZahl(bytes / 1024 / 1024, 2)} MB`;
}

/**
 * Reiter „Dokumente" (Spezifikation 15.5) — Filter Art/ASP/Anlage, Tabelle
 * der abgelegten PDF-Dokumente, Kopfknopf „Dokumente erzeugen" mit Auswahl
 * der Arten und Hefte-Schaltern, Karte „Letzter Lauf".
 */
export function Dokumente({ api, projektId, anlagen, asps }: {
  api: ApiClient; projektId: string; anlagen: MsrAnlageZeile[]; asps: MsrAspZeile[];
}) {
  const [artFilter, setArtFilter] = useState('');
  const [aspFilter, setAspFilter] = useState('');
  const [anlageFilter, setAnlageFilter] = useState('');

  const [zeilen, setZeilen] = useState<MsrDokument[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const [letzterLauf, setLetzterLauf] = useState<MsrDokumentlauf | null>(null);

  const kennungJeAnlage = useMemo(
    () => new Map(anlagen.map((a) => [a.id, a.kennung])), [anlagen]);
  const kennungJeAsp = useMemo(() => new Map(asps.map((a) => [a.id, a.kennung])), [asps]);

  const neuLaden = () => {
    setFehler(null);
    api.msrDokumente(projektId, {
      art: (artFilter || undefined) as MsrDokumentart | undefined,
      asp: aspFilter || undefined,
      anlage: anlageFilter || undefined,
    }).then(setZeilen)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  };

  const letztenLaufLaden = () => {
    api.msrDokumentlaeufe(projektId, 1, 1)
      .then((s) => setLetzterLauf(s.zeilen[0] ?? null))
      .catch(() => setLetzterLauf(null));
  };

  useEffect(neuLaden, [api, projektId, artFilter, aspFilter, anlageFilter]);
  useEffect(letztenLaufLaden, [api, projektId]);

  // ── Kopfknopf „Dokumente erzeugen" ─────────────────────────────────────
  const [formularOffen, setFormularOffen] = useState(false);
  const [gewaehlteArten, setGewaehlteArten] = useState<Set<MsrDokumentart>>(new Set());
  const [hefte, setHefte] = useState(false);
  const [projektheft, setProjektheft] = useState(false);
  const [erzeugtLaeuft, setErzeugtLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState<MsrDokumentlauf | null>(null);
  const [erzeugenFehler, setErzeugenFehler] = useState<string | null>(null);

  const artUmschalten = (a: MsrDokumentart) => setGewaehlteArten((s) => {
    const neu = new Set(s);
    if (neu.has(a)) neu.delete(a); else neu.add(a);
    return neu;
  });

  const erzeugen = async () => {
    setErzeugtLaeuft(true);
    setErzeugenFehler(null);
    setErgebnis(null);
    try {
      const r = await api.msrDokumenteErzeugen(projektId, {
        arten: gewaehlteArten.size > 0 ? Array.from(gewaehlteArten) : undefined,
        hefte, projektheft,
      });
      setErgebnis(r);
      neuLaden();
      letztenLaufLaden();
    } catch (e) {
      setErzeugenFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setErzeugtLaeuft(false);
    }
  };

  const anzeigen = async (dokumentId: string) => {
    try {
      const url = await api.msrDokumentAnzeigen(projektId, dokumentId);
      window.open(url, '_blank');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  const herunterladen = async (dokumentId: string) => {
    try {
      await api.msrDokumentHerunterladen(projektId, dokumentId);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  const kennungAnzeigen = (d: MsrDokument): string => {
    if (d.ziel === 'anlage' && d.zielId) return kennungJeAnlage.get(d.zielId) ?? d.kennung ?? '—';
    if (d.ziel === 'asp' && d.zielId) return kennungJeAsp.get(d.zielId) ?? d.kennung ?? '—';
    return d.kennung ?? 'Projekt';
  };

  const artenFeld = (titel: string, arten: MsrDokumentart[]) => (
    <FormSection titel={titel} spalten={1}>
      {arten.map((a) => (
        <Feld key={a} art="haken" beschriftung={ART_TEXT[a]}
          wert={gewaehlteArten.has(a)} beiAenderung={() => artUmschalten(a)} />
      ))}
    </FormSection>
  );

  const gegenprobenListe = (
    gegenproben: { schluessel: string; ergebnis: string; aussage: string }[],
  ) => (
    <ul style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.9 }}>
      {gegenproben.map((g) => (
        <li key={g.schluessel}>
          <StatusBadge
            klein
            zustand={g.ergebnis === 'ok' ? 'gut' : g.ergebnis === 'warnung' ? 'warnung' : 'fehler'}
            text={g.ergebnis === 'ok' ? 'in Ordnung'
              : g.ergebnis === 'warnung' ? 'Prüfen' : 'Fehler'}
          />{' '}{g.aussage}
        </li>
      ))}
    </ul>
  );

  const filter: FilterDefinition[] = [
    {
      schluessel: 'art', beschriftung: 'Art', art: 'auswahl',
      optionen: Object.entries(ART_TEXT).map(([k, v]) => ({ wert: k, beschriftung: v })),
    },
    {
      schluessel: 'anlage', beschriftung: 'Anlage', art: 'auswahl',
      optionen: anlagen.map((a) => ({ wert: a.id, beschriftung: a.kennung })),
    },
    {
      schluessel: 'asp', beschriftung: 'ASP', art: 'auswahl',
      optionen: asps.map((a) => ({ wert: a.id, beschriftung: a.kennung })),
    },
  ];

  const filterWerte: FilterWerte = {
    art: artFilter, anlage: anlageFilter, asp: aspFilter,
  };

  const entfallen = (zeilen ?? []).filter((d) => zielkennzeichenText(d.hinweis) !== null).length;

  const spalten: Spalte<MsrDokument>[] = [
    { schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true,
      wert: (d) => kennungAnzeigen(d) },
    {
      // F127 (R53): Dokumente entfallener oder gelöschter Ziele bleiben in der
      // Liste, tragen aber eine deutliche Marke - niemand soll sie für aktuell halten.
      schluessel: 'ziel', titel: 'Gültigkeit', breite: '150px', immerSichtbar: true,
      wert: (d) => zielkennzeichenText(d.hinweis) ?? '',
      inhalt: (d) => {
        const text = zielkennzeichenText(d.hinweis);
        return text
          ? <StatusBadge zustand="warnung" klein text={text} titel={ZIELKENNZEICHEN_TITEL} />
          : null;
      },
    },
    { schluessel: 'art', titel: 'Art', wert: (d) => ART_TEXT[d.art] },
    { schluessel: 'dateiname', titel: 'Dateiname', wert: (d) => d.dateiname },
    { schluessel: 'seiten', titel: 'Seiten', art: 'zahl', wert: (d) => d.seiten },
    {
      schluessel: 'bytes', titel: 'Größe', ausrichtung: 'rechts', breite: '110px',
      wert: (d) => d.bytes, inhalt: (d) => groesseText(d.bytes),
    },
    {
      schluessel: 'erzeugtAm', titel: 'Stand', art: 'datum', breite: '170px',
      wert: (d) => d.erzeugtAm,
      inhalt: (d) => zeitpunkt(d.erzeugtAm),
    },
    {
      schluessel: 'aktionen', titel: 'Blatt', breite: '210px', sortierbar: false,
      wert: () => '',
      inhalt: (d) => (
        <>
          <button type="button" className="m-knopf" style={{ marginRight: 6 }}
            onClick={() => anzeigen(d.id)}>Anzeigen</button>
          <button type="button" className="m-knopf"
            onClick={() => herunterladen(d.id)}>Herunterladen</button>
        </>
      ),
    },
  ];

  return (
    <>
      <ContentSection
        titel="Dokumente"
        beschreibung={'PDF-Blätter und -Hefte aus dem letzten Ableitungslauf '
          + '(Spezifikation Abschnitt 15) — ein Inhalt, zwei Ausgaben: die Seite zeigt '
          + 'SVG/HTML, das Dokument zeigt dasselbe als Blatt mit Rahmen und Schriftfeld.'}
        aktionen={(
          <>
            {/* „Alle als ZIP" nimmt DEN AKTUELLEN FILTER der Tabelle unten -
                nicht immer alles. Ein Knopf, der etwas anderes packt, als
                darueber steht, wird genau einmal geglaubt. */}
            <UnterlagenZipKnopf
              beschriftung={artFilter || aspFilter || anlageFilter
                ? 'Gefilterte als ZIP'
                : 'Alle als ZIP'}
              dateien={zeilen?.length}
              laden={() => api.msrDokumenteZip(projektId, {
                art: (artFilter || undefined) as MsrDokumentart | undefined,
                asp: aspFilter || undefined,
                anlage: anlageFilter || undefined,
              })}
            />
            <button type="button" className="m-knopf m-knopf-haupt"
              onClick={() => setFormularOffen(true)}>
              Dokumente erzeugen
            </button>
          </>
        )}
      >
        {/* Vierzehn Haken in drei Spalten: das gehört nach Abschnitt 16 ins
            Schubfach und nicht in einen aufklappenden Kasten über der Liste. */}
        <Drawer
          offen={formularOffen}
          titel="Dokumente erzeugen"
          beschreibung="Ohne Auswahl entstehen alle Einzelblätter je Anlage und ASP (ohne Hefte)."
          beiSchliessen={() => setFormularOffen(false)}
          fuss={(
            <>
              <button type="button" className="m-knopf m-knopf-haupt"
                disabled={erzeugtLaeuft} onClick={erzeugen}>
                {erzeugtLaeuft ? 'läuft …' : 'Jetzt erzeugen'}
              </button>
              <button type="button" className="m-knopf"
                onClick={() => setFormularOffen(false)}>Abbrechen</button>
            </>
          )}
        >
          {artenFeld('Je Anlage', ARTEN_ANLAGE)}
          {artenFeld('Je ASP', ARTEN_ASP)}
          {artenFeld('Projekt', ARTEN_PROJEKT)}

          <FormSection titel="Zusätzlich" spalten={1}>
            <Feld art="haken" beschriftung="Hefte (Anlage/ASP) zusätzlich"
              wert={hefte} beiAenderung={(w) => setHefte(w as boolean)} />
            <Feld art="haken" beschriftung="Projektheft zusätzlich"
              wert={projektheft} beiAenderung={(w) => setProjektheft(w as boolean)} />
          </FormSection>

          {erzeugenFehler && (
            <ErrorState
              klein
              meldung="Die Dokumente konnten nicht erzeugt werden."
              technisch={erzeugenFehler}
              beiErneutVersuchen={erzeugen}
            />
          )}
        </Drawer>

        {erzeugenFehler && !formularOffen && (
          <ErrorState
            meldung="Die Dokumente konnten nicht erzeugt werden."
            technisch={erzeugenFehler}
            beiErneutVersuchen={() => setFormularOffen(true)}
          />
        )}

        {ergebnis && (
          <div className="hinweis" style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              Lauf {zeitpunkt(ergebnis.gestartetAm)}
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12 }}>
              {Object.entries(ergebnis.zahlen).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => (
                <span key={k}><strong>{k}</strong>: {v}</span>
              ))}
            </div>
            {gegenprobenListe(ergebnis.gegenproben)}
            {ergebnis.hinweise.length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {ergebnis.hinweise.map((h, i) => <li key={i}>{h}</li>)}
              </ul>
            )}
          </div>
        )}
      </ContentSection>

      {letzterLauf && (
        <ContentSection
          titel="Letzter Lauf"
          meta={zeitpunkt(letzterLauf.gestartetAm)
            + (letzterLauf.ausgefuehrtVon ? ` — ${letzterLauf.ausgefuehrtVon}` : '')
            + (letzterLauf.arten.length > 0
              ? ` — Arten: ${letzterLauf.arten.join(', ')}` : '')}
        >
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12 }}>
            {Object.entries(letzterLauf.zahlen).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => (
              <span key={k}><strong>{k}</strong>: {v}</span>
            ))}
          </div>
          {gegenprobenListe(letzterLauf.gegenproben)}
        </ContentSection>
      )}

      <ContentSection titel="Abgelegte Dokumente">
        <FilterBar
          filter={filter}
          werte={filterWerte}
          beiAenderung={(w) => {
            setArtFilter(String(w.art ?? ''));
            setAnlageFilter(String(w.anlage ?? ''));
            setAspFilter(String(w.asp ?? ''));
          }}
          inLeiste={3}
        />

        {entfallen > 0 && (
          <div className="hinweis m-zielkennzeichen" style={{ margin: '8px 0' }}>
            {entfallen === 1
              ? '1 Dokument gehört zu einem entfallenen oder gelöschten Ziel'
              : `${entfallen} Dokumente gehören zu entfallenen oder gelöschten Zielen`}
            {' '}(Spalte „Gültigkeit“). Sie sind nicht mehr aktuell und liegen im ZIP im Ordner „Entfallen/“.
          </div>
        )}

        <DataTable
          name="Dokumente"
          spalten={spalten}
          zeilen={zeilen ?? []}
          schluessel={(d) => d.id}
          laedt={!zeilen && !fehler}
          fehler={fehler ?? undefined}
          beiErneutVersuchen={neuLaden}
          dicht
          ausfuhr
          ausfuhrDateiname="msr-dokumente"
          leer={(
            <EmptyState
              titel={artFilter || anlageFilter || aspFilter
                ? 'Kein Dokument passt zu diesen Filtern.'
                : 'Noch kein Dokument abgelegt.'}
              erklaerung={artFilter || anlageFilter || aspFilter
                ? 'Die gesetzten Filter grenzen die Liste auf null Zeilen ein.'
                : 'Die PDF-Blätter entstehen über „Dokumente erzeugen" aus dem letzten '
                  + 'Ableitungslauf.'}
              aktion={(
                <button type="button" className="m-knopf m-knopf-haupt"
                  onClick={() => setFormularOffen(true)}>
                  Dokumente erzeugen
                </button>
              )}
            />
          )}
        />
      </ContentSection>
    </>
  );
}
